/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { contentModeCookieName } from "@insite/client-framework/Common/ContentMode";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import {getProp} from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xAppState } from "@monsma/App/Store/Reducers";
import { isItCheckoutPage } from "@monsma/App/Utilities/helpers";
import { screen } from "@monsma/XDL/Helpers";
import { companyLogoItem } from "@monsma/XDL/Utilities/sharedStyles";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: xAppState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    canAddWidget: state.context.permissions?.canAddWidget,
    isSticky: state.xHeader.isSticky,
    stickyMenu: state.xHeader.stickyMenu,
    notificationBar: state.xHeader.notificationBar,
    pageType: getCurrentPage(state).type,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: MobiusPageProps;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
    placeholder?: InjectableCss;
    headerContainer?: InjectableCss;
    secondaryContainer?: InjectableCss;
    primaryContainer?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, true, true, true],
        padding: 0,
        css: css`
            position: relative;
            z-index: ${getProp("theme.zIndex.header")};
        `,
    },
    container: {
        as: "div",
        fullWidth: [true, true, false, false, false],
        padding: 0,
        background: "transparent",
        css: css`
            display: flex;
        `,
    },
    secondaryContainer: {
        css: css`
            display: flex;
            background-color: ${getColor("common.accent")};
            box-shadow: inset 0 -1px 0 0 ${getColor("common.border")};
            ${({ canAddWidget }: any) => !canAddWidget && `height: 50px;`}

            ${screen("lte", "md")} {
                height: 0;
            }

            &.hide {
                display: none;
            }
        `,
    },
    primaryContainer: {
        css: css`
            display: flex;
            box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.1);
            background-color: white;
            ${({ canAddWidget }: any) => !canAddWidget && `height: 69px;`}
            ${screen("lte", "md")} {
                ${({ canAddWidget }: any) => !canAddWidget && `height: 64px;`}
            }
        `,
    },
    headerContainer: {
        css: css`
            position: fixed;
            top: 0;
            z-index: 10;
            width: 100%;

            &.edit-cms {
                position: absolute;
            }
        `,
    },
    placeholder: {
        css: css`
            height: calc(70px + 50px);
            ${({ notificationBar }: any) => notificationBar && `height: calc(70px + 50px + 40px);`}

            &.hide-secondary-navigation {
                height: 70px;
                ${({ notificationBar }: any) => notificationBar && `height: calc(70px + 40px);`}
            }

            &&& {
                ${screen("lte", "md")} {
                    height: 64px;
                    ${({ notificationBar }: any) => notificationBar && `height: calc(64px + 40px);`}
                }
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 25px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    canAddWidget, // xng-change
    isSticky, // xng-change
    stickyMenu, // xng-change
    notificationBar, // xng-change
    pageType, // xng-change
}: Props) => {
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    const afterHeader = React.createRef<HTMLSpanElement>();
    const extra = { isSticky, stickyMenu, notificationBar, canAddWidget } as any;
    const isCmsEditing = getCookie(contentModeCookieName) === "Editing";

    return (
        <EagerLoadingLogoProvider value={true}>
            <StyledWrapper {...companyLogoItem}>
                <img src={siteMessage("Custom_Company_Logo") as any} alt={translate("Company Logo")} />
            </StyledWrapper>
            <Page as="header" isHeader={true} {...styles.page}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <StyledWrapper
                    {...styles.placeholder}
                    {...extra}
                    className={`${isItCheckoutPage(pageType) && "hide-secondary-navigation"}`}
                />
                <StyledWrapper {...styles.headerContainer} {...extra} className={`${isCmsEditing && "edit-cms"}`}>
                    <Zone contentId={id} zoneName="NotificationBar" />
                    <StyledWrapper
                        {...styles.secondaryContainer}
                        {...extra}
                        className={`${isItCheckoutPage(pageType) && "hide"}`}
                    >
                        <Page as="div" {...styles.container}>
                            <Zone contentId={id} zoneName="SecondaryNavigation" />
                        </Page>
                    </StyledWrapper>
                    <StyledWrapper {...styles.primaryContainer} {...extra}>
                        <Page as="div" {...styles.container}>
                            <Zone contentId={id} zoneName="MainNavigation" />
                        </Page>
                    </StyledWrapper>
                </StyledWrapper>
                <Zone contentId={id} zoneName="Breadcrumb" />
                <span ref={afterHeader} tabIndex={-1} />
            </Page>
        </EagerLoadingLogoProvider>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
